import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  AccordionDetails,
  AccordionSummary,
  Grid,
  TextField as Field,
  Typography,
  Box
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import Accordion from '@mui/material/Accordion';
import { Info, CloudUpload } from '@mui/icons-material';
import { colors } from '../../../Utilities/LenoxColors';

const styles = (theme) => ({
  container: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    width: '100%',
    padding: '10px',
    margin: '8px',
    borderRadius: '4px',
    position: 'relative'
  },
  label: {
    marginLeft: '10px',
    padding: '4px',
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.54)',
    backgroundColor: 'white',
    position: 'absolute',
    top: '-8px',
    left: '-10px',
    transform: 'translate(14px, -6px) scale(0.75)'
  },
  margin: {
    margin: theme.spacing(1)
  },
  textField: {
    flexBasis: 200,
    width: '100%',
    color: `${colors.lenoxLight1}!important`,
    display: 'none'
  },
  dropZone: {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    padding: '10px 5px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(25, 118, 210, 0.04)'
    }
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    '& $dropZoneText': {
      color: theme.palette.primary.main
    }
  },
  dropZoneText: {
    color: '#666',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1)
  },
  dropZoneSubtext: {
    color: '#999',
    fontSize: '0.875rem'
  },
  hiddenInput: {
    display: 'none'
  },
  fileName: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderRadius: '4px',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '8px'
  }
});

const FileUploadDragDrop = ({
  name,
  classes,
  exampleFormat,
  example,
  label,
  fullwidth,
  error,
  message,
  onChange
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    onChange(name, file);
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDropZoneClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Grid container alignItems="center" justifyContent="left">
      <div className={classes.container}>
        <div className={classes.label}>{label}</div>

        <Box
          className={`${classes.dropZone} ${
            isDragOver ? classes.dropZoneActive : ''
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleDropZoneClick}
        >
          <Typography className={classes.dropZoneText}>
            <CloudUpload />
            Drag & drop a file here, or click to select
          </Typography>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          className={classes.hiddenInput}
          onChange={handleFileInputChange}
        />

        <Field
          label={label}
          fullwidth={fullwidth}
          error={error}
          id={name}
          className={classNames(classes.textField)}
          variant="outlined"
          helperText=""
          type="file"
          margin="dense"
          InputLabelProps={{ shrink: true }}
          onChange={handleFileInputChange}
        />

        {selectedFile && (
          <Box className={classes.fileName}>
            <Typography variant="body2">
              <strong>Selected file:</strong> {selectedFile.name}
            </Typography>
            <Typography variant="caption">
              Size: {(selectedFile.size / 1024).toFixed(2)} KB
            </Typography>
          </Box>
        )}

        {error && message && <div className={classes.error}>{message}</div>}

        {exampleFormat && (
          <Accordion>
            <AccordionSummary
              expandIcon={<Info />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography>Example Format</Typography>
            </AccordionSummary>
            <AccordionDetails>{example}</AccordionDetails>
          </Accordion>
        )}
      </div>
    </Grid>
  );
};

FileUploadDragDrop.propTypes = {
  name: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  exampleFormat: PropTypes.bool,
  example: PropTypes.node,
  label: PropTypes.string,
  fullwidth: PropTypes.bool,
  error: PropTypes.bool,
  message: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

export default withStyles(FileUploadDragDrop, styles);
